import { Card } from './card';
import { PileId, PileType } from '../logic-types';
import { GameLogicDepth } from '../game-logic-depth';

export class Pile {
  public readonly id: PileId;
  public readonly type: PileType;

  public get hasCards(): boolean {
    return this.cards.length > 0;
  }

  public get cardCount(): number {
    return this.cards.length;
  }

  public get lastCard(): Card | undefined {
    return this.cards.length > 0 ? this.cards[this.cards.length - 1] : undefined;
  }

  public get allCards(): Card[] {
    return this.cards;
  }

  private cards: Card[] = [];

  constructor(pileId: PileId, pileType: PileType) {
    this.id = pileId;
    this.type = pileType;
  }

  public static constructFromClone(pileId: PileId, pileType: PileType, existing: Pile) {
    const newPile = new Pile(pileId, pileType);
    newPile.cards = existing.cards.map((card) => card.clone());
    return newPile;
  }

  public getCardByCardId(cardId: string): Card | undefined {
    return this.cards.find((card) => card.cardId?.id === cardId);
  }

  public getCardByCardIndex(index: number): Card | undefined {
    return this.cards.find((card) => card.index === index);
  }

  public containsCardId(cardId: string): boolean {
    return this.cards.some((card) => card.cardId?.id === cardId);
  }

  public containsCardIndex(index: number): boolean {
    return this.cards.some((card) => card.index === index);
  }

  public parentCard(childCard: Card): Card | undefined {
    const indexOfChild = this.cards.indexOf(childCard);
    if (indexOfChild <= 0) {
      return undefined;
    }

    const parentIndex = indexOfChild - 1;
    return this.cards[parentIndex];
  }

  public addCard(card: Card) {
    this.cards.push(card);
  }

  public removeCard(card: Card) {
    const foundIndex = this.cards.findIndex((item) => item === card);
    if (foundIndex !== -1) {
      this.cards.splice(foundIndex, 1);
    }
  }

  public removeAllCards() {
    this.cards.length = 0;
  }

  public moveCards(card: Card): Card[] {
    const cardsToMove: Card[] = [];

    const cardIndex = this.cards.findIndex((item) => item === card);
    if (cardIndex === -1) {
      return cardsToMove;
    }

    for (let index = cardIndex; index < this.cardCount; ++index) {
      cardsToMove.push(this.cards[index]);
    }

    return cardsToMove;
  }

  public static closeCardsBackToDeck(
    waste: Pile,
    deck: Pile,
    gameLogicDepth: GameLogicDepth,
  ): number[] {
    const originalWasteCardIndices = waste.cards.map((card) => card.index);

    for (let i = waste.cardCount - 1; i >= 0; i--) {
      gameLogicDepth.updateCardDepth(waste.cards[i].index);
      deck.addCard(waste.cards[i]);
      waste.cards[i].closeCard();
    }
    return originalWasteCardIndices;
  }

  public combine(cards: Card[]) {
    cards.push(...this.cards);
  }

  public printLog(gameLogicDepth: GameLogicDepth): void {
    function pileIdName(value: number): string {
      return PileId[value] as keyof typeof PileId;
    }

    console.log(
      `${pileIdName(this.id)} - ${this.cards
        .map(
          (card) =>
            `[${card.index} | ${gameLogicDepth.cardDepth(card)} | ${
              card.cardId?.id ?? 'undefined'
            }${card.isFaceUp ? ' f' : ''}]`,
        )
        .join(', ')}`,
    );
  }
}
